/**
 * React hook for calculating exposure on-demand
 * Uses data from existing position hooks and calculates exposure client-side
 */

import React from 'react';
import { useDemandPositions } from './useDemandPositions';
import { useSupplyPositions } from './useSupplyPositions';
import { useFinancialPositionsStore } from '../components/Stores/FinancialPositionStore';
import { 
  calculateExposure, 
  calculateTotalExposure,
  calculateExposureByFormula,
  ExposureBreakdown 
} from '../utils/exposureCalculation';
import { getExposureWindow } from '../utils/exposureHorizon';
import { useAccountStore } from '../components/Stores/accountStore';
import { useMaterialsStore } from '../components/Stores/MaterialsStore';
import { useFormulasPrices } from './useFormulasPrices';

export interface UseExposureCalculationOptions {
  formulaId?: string;  // If specified, calculate for specific formula. If undefined, calculate total.
  useHorizon?: boolean; // If true, apply viewing horizon filter
}

export interface UseExposureCalculationResult {
  exposure: ExposureBreakdown | null;
  exposureByFormula: Record<string, ExposureBreakdown> | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook to calculate exposure from current position data
 * Calculates on-demand, no pre-calculation or storage
 */
export const useExposureCalculation = (
  options: UseExposureCalculationOptions = {}
): UseExposureCalculationResult => {
  const { formulaId, useHorizon = true } = options;
  const { account } = useAccountStore();
  const { materialsMap } = useMaterialsStore();
  const { data: pricesData } = useFormulasPrices();
  const pricesMap = pricesData?.pricesMap || {};
  const spotPriceAsOfDate = pricesData?.asOfDate || null;

  // Fetch all position data
  const { data: demandPositions, isLoading: demandLoading, error: demandError } = useDemandPositions();
  const { data: supplyPositions, isLoading: supplyLoading, error: supplyError } = useSupplyPositions();
  // Read derived hedges from store (populated by useFinancialPositions in Dashboard)
  // Note: empty array is valid (account has no hedges) — don't treat as "not loaded"
  const { hedgePositions } = useFinancialPositionsStore();
  const hedgesLoading = false;
  const hedgesError = null;

  // Get base currency and horizon
  const baseCurrency = account?.baseCurrency || 'USD';
  const horizonMonths = account?.viewingHorizonMonths || 12;
  
  // Calculate exposure window if needed
  const horizonWindow = React.useMemo(() => {
    if (!useHorizon) return undefined;
    return getExposureWindow(horizonMonths);
  }, [useHorizon, horizonMonths]);

  // Filter out ratio POs from supply (no fixed price, excluded from calcs)
  const filteredSupply = React.useMemo(
    () => supplyPositions?.filter(s => s.supply_type !== 'ratio_po'),
    [supplyPositions],
  );

  // Calculate exposure
  const isLoading = demandLoading || supplyLoading || hedgesLoading;
  const exposure = React.useMemo(() => {
    if (isLoading || !demandPositions || !filteredSupply || !materialsMap || !pricesMap) {
      return null;
    }

    if (formulaId) {
      // Calculate for specific formula
      return calculateExposure(
        demandPositions,
        filteredSupply,
        hedgePositions,
        pricesMap,
        materialsMap,
        baseCurrency,
        spotPriceAsOfDate,
        formulaId,
        horizonWindow,
        horizonMonths,
      );
    } else {
      // Calculate total across all formulas
      return calculateTotalExposure(
        demandPositions,
        filteredSupply,
        hedgePositions,
        pricesMap,
        materialsMap,
        baseCurrency,
        spotPriceAsOfDate,
        horizonWindow,
        horizonMonths,
      );
    }
  }, [isLoading, demandPositions, filteredSupply, hedgePositions, pricesMap, materialsMap, baseCurrency, spotPriceAsOfDate, formulaId, horizonWindow, horizonMonths]);

  // Calculate exposure by formula (for general view)
  const exposureByFormula = React.useMemo(() => {
    if (!demandPositions || !filteredSupply || !hedgePositions || !materialsMap || !pricesMap) {
      return null;
    }

    return calculateExposureByFormula(
      demandPositions,
      filteredSupply,
      hedgePositions,
      pricesMap,
      materialsMap,
      baseCurrency,
      spotPriceAsOfDate,
      horizonWindow,
      horizonMonths,
    );
  }, [demandPositions, filteredSupply, hedgePositions, pricesMap, materialsMap, baseCurrency, spotPriceAsOfDate, horizonWindow, horizonMonths]);

  const error = demandError || supplyError || hedgesError;

  return {
    exposure,
    exposureByFormula,
    isLoading,
    error: error as Error | null,
  };
};
